import {
  AccuratePostingTransaction,
} from "../types/accuratePosting";

export interface AccuratePostingValidationError {
  field: string;
  message: string;
  severity: "error";
}

const error = (field: string, message: string): AccuratePostingValidationError => ({
  field,
  message,
  severity: "error",
});

const isBlank = (value: unknown): boolean =>
  typeof value !== "string" || value.trim().length === 0;

const isPositiveQuantity = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value > 0;

const normalizeText = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

const asRecord = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === "object" ? (value as Record<string, unknown>) : {};

const allowedSerialNumberTypes = new Set(["UNIQUE", "BATCH"]);
const allowedModuleTypes = new Set(["RI", "FGS", "RO"]);

const detailItemField = (detailIndex: number): string => `detailItem[${detailIndex}]`;

const serialRowField = (detailIndex: number, serialIndex: number): string =>
  `${detailItemField(detailIndex)}.detailSerialNumber[${serialIndex}]`;

export const validateAccuratePostingTransaction = (
  transaction: AccuratePostingTransaction
): AccuratePostingValidationError[] => {
  const errors: AccuratePostingValidationError[] = [];

  const moduleType = normalizeText(transaction.moduleType);
  const jobOrder = asRecord(transaction.jobOrder);
  const workOrder = asRecord(transaction.workOrder);
  const jobOrderNumber =
    normalizeText(transaction.jobOrderNumber) ||
    normalizeText(jobOrder.number) ||
    normalizeText(workOrder.number);

  if (moduleType.length === 0) {
    errors.push(error("moduleType", "moduleType is required"));
  } else if (!allowedModuleTypes.has(moduleType)) {
    errors.push(error("moduleType", "moduleType must be RI, FGS, or RO"));
  }

  if (isBlank(transaction.number)) {
    errors.push(error("number", "number is required"));
  }

  if (transaction.id === undefined || transaction.id === null) {
    errors.push(error("id", "id is required"));
  }

  if (transaction.branchId === undefined || transaction.branchId === null) {
    errors.push(error("branchId", "branchId is required"));
  }

  if (isBlank(transaction.transDate)) {
    errors.push(error("transDate", "transDate is required"));
  }

  if (moduleType === "RI") {
    if (isBlank(transaction.receiveNumber)) {
      errors.push(error("receiveNumber", "receiveNumber is required for RI"));
    }
    if (isBlank(transaction.vendorNo)) {
      errors.push(error("vendorNo", "vendorNo is required for RI"));
    }
  }

  if (moduleType === "FGS" && isBlank(jobOrderNumber)) {
    errors.push(error("jobOrderNumber", "jobOrderNumber is required for FGS"));
  }

  if (moduleType === "RO") {
    if (isBlank(jobOrderNumber)) {
      errors.push(error("jobOrderNumber", "jobOrderNumber is required for RO"));
    }
    if (isBlank(transaction.rollOverType)) {
      errors.push(error("rollOverType", "rollOverType is required for RO"));
    }
  }

  if (!Array.isArray(transaction.detailItem) || transaction.detailItem.length === 0) {
    errors.push(error("detailItem", "detailItem is required"));
    return errors;
  }

  const seenSerialNumbers = new Set<string>();

  transaction.detailItem.forEach((detailItem, detailIndex) => {
    let hasRowError = false;

    if (detailItem.id === undefined || detailItem.id === null) {
      errors.push(error(`${detailItemField(detailIndex)}.id`, "id is required"));
      hasRowError = true;
    }

    if (isBlank(detailItem.itemNo)) {
      errors.push(error(`${detailItemField(detailIndex)}.itemNo`, "itemNo is required"));
      hasRowError = true;
    }

    if (!isPositiveQuantity(detailItem.quantity)) {
      errors.push(error(`${detailItemField(detailIndex)}.quantity`, "quantity must be greater than 0"));
      hasRowError = true;
    }

    if (detailItem.manageSN === false) return;

    const serialRows = Array.isArray(detailItem.detailSerialNumber)
      ? detailItem.detailSerialNumber
      : [];
    const serialNumberType = normalizeText(detailItem.serialNumberType);
    const hasValidSerialNumberType = allowedSerialNumberTypes.has(serialNumberType);

    if (!hasValidSerialNumberType) {
      errors.push(
        error(
          `${detailItemField(detailIndex)}.serialNumberType`,
          "serialNumberType must be UNIQUE or BATCH"
        )
      );
    }

    if (serialRows.length === 0) {
      errors.push(
        error(
          `${detailItemField(detailIndex)}.detailSerialNumber`,
          "detailSerialNumber is required"
        )
      );
      return;
    }

    let serialQuantityTotal = 0;

    serialRows.forEach((serialRow, serialIndex) => {
      const rowField = serialRowField(detailIndex, serialIndex);
      const serialNumberNo = normalizeText(serialRow.serialNumberNo);

      if (serialNumberNo.length === 0) {
        errors.push(error(`${rowField}.serialNumberNo`, "serialNumberNo is required"));
        hasRowError = true;
        return;
      }

      if (seenSerialNumbers.has(serialNumberNo)) {
        errors.push(error(`${rowField}.serialNumberNo`, "duplicate serial number"));
        hasRowError = true;
        return;
      }

      seenSerialNumbers.add(serialNumberNo);

      if (!isPositiveQuantity(serialRow.quantity)) {
        errors.push(error(`${rowField}.quantity`, "quantity must be greater than 0"));
        hasRowError = true;
        return;
      }

      serialQuantityTotal += serialRow.quantity;

      if (serialNumberType === "UNIQUE" && serialRow.quantity !== 1) {
        errors.push(error(`${rowField}.quantity`, "UNIQUE rows must have quantity = 1"));
        hasRowError = true;
      }
    });

    if (hasRowError) {
      return;
    }

    const detailQuantity = detailItem.quantity;

    if (hasValidSerialNumberType && serialQuantityTotal !== detailQuantity) {
      errors.push(
        error(
          `${detailItemField(detailIndex)}.detailSerialNumber`,
          `${serialNumberType} total quantity must match detail quantity`
        )
      );
    }
  });

  return errors;
};
